// apps/admin/src/utils/worksheetValidation.ts
// Comprehensive worksheet validation utilities
// Created: 2025-11-24
// Purpose: Production-grade validation for multi-sheet Excel processing
// Principle: NEVER silently filter data - always validate explicitly with clear errors

import * as XLSX from 'xlsx';

/**
 * Worksheet validation result with explicit success/failure tracking
 */
export interface WorksheetValidationResult {
  isValid: boolean;
  headers: string[];
  rowCount: number;
  issues: string[];
  metadata: {
    hasFormulaColumns: boolean;
    hasMergedCells: boolean;
    estimatedDataRows: number;
    emptyHeaderCount: number;
    suspiciousHeaderCount: number;
  };
}

/**
 * Required columns by worksheet type
 * Used for type-specific validation
 */
const REQUIRED_COLUMNS_BY_TYPE: Record<string, string[]> = {
  upfront: ['Consultant', 'Loan Number', 'Settlement Date', 'Commission'],
  trail: ['Consultant', 'Loan Number', 'Loan Balance', 'Trail'],
  nil: ['Consultant', 'Loan Number', 'Reason'],
  summary: ['Total', 'GST']
};

/**
 * Validates worksheet headers with explicit error handling
 *
 * CRITICAL: This function NEVER silently filters data
 * - Throws clear errors for invalid structure
 * - Logs warnings for suspicious patterns
 * - Returns only validated, non-empty headers
 *
 * @throws Error if worksheet has no valid headers
 */
export function validateWorksheetHeaders(
  worksheet: XLSX.WorkSheet,
  sheetName: string
): string[] {
  // Extract first row as potential headers
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const firstRow: any[] = [];

  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cell = worksheet[cellAddress];
    firstRow.push(cell ? cell.v : undefined);
  }

  // Explicit validation with clear error messages
  if (!firstRow || firstRow.length === 0) {
    throw new Error(
      `Worksheet "${sheetName}" appears to be empty. Expected column headers in the first row.`
    );
  }

  // Filter and validate headers with type safety
  const validHeaders: string[] = [];
  const invalidHeaders: Array<{ index: number; value: any }> = [];

  firstRow.forEach((header, index) => {
    if (header === undefined || header === null) {
      invalidHeaders.push({ index, value: header });
      return;
    }

    const headerStr = String(header).trim();
    if (headerStr.length === 0) {
      invalidHeaders.push({ index, value: header });
      return;
    }

    // Log suspicious headers for debugging
    if (headerStr.length > 100) {
      console.warn(
        `Suspicious header in column ${index + 1} of sheet "${sheetName}": "${headerStr.substring(0, 50)}..." (${headerStr.length} chars)`
      );
    }

    validHeaders.push(headerStr);
  });

  // Ensure we have at least some valid headers
  if (validHeaders.length === 0) {
    throw new Error(
      `Worksheet "${sheetName}" has no valid column headers. First row contains only empty or invalid values.`
    );
  }

  // Warn if too many headers are invalid
  const invalidCount = invalidHeaders.length;
  if (invalidCount > firstRow.length * 0.5) {
    console.warn(
      `Worksheet "${sheetName}" has ${invalidCount}/${firstRow.length} invalid headers. This may indicate incorrect sheet structure or header row position.`
    );
    console.warn(`Invalid header positions:`, invalidHeaders.map(h => `Column ${h.index + 1}: ${h.value}`));
  }

  return validHeaders;
}

/**
 * Get required columns for a specific worksheet type
 */
function getRequiredColumnsForType(type: string): string[] {
  return REQUIRED_COLUMNS_BY_TYPE[type] || [];
}

/**
 * Check if worksheet contains formula columns
 * Formulas may need special handling during processing
 */
function checkForFormulas(worksheet: XLSX.WorkSheet, range: XLSX.Range): boolean {
  // Check first 10 data rows (skip header)
  for (let row = range.s.r + 1; row <= Math.min(range.s.r + 10, range.e.r); row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (cell && cell.f) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Count non-empty data rows (excluding header)
 * Used to estimate actual data volume vs. empty rows
 */
function countNonEmptyRows(worksheet: XLSX.WorkSheet, range: XLSX.Range): number {
  let nonEmptyRows = 0;

  // Start from row 1 (skip header at row 0)
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    let hasData = false;

    for (let col = range.s.c; col <= range.e.c; col++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
      if (cell && cell.v !== undefined && cell.v !== null && String(cell.v).trim() !== '') {
        hasData = true;
        break;
      }
    }

    if (hasData) {
      nonEmptyRows++;
    }
  }

  return nonEmptyRows;
}

/**
 * Comprehensive worksheet structure validation
 *
 * Validates:
 * - Worksheet exists and has data
 * - Header row is valid
 * - Minimum row count requirements
 * - Type-specific column requirements
 * - Structural issues (merged cells, formulas)
 *
 * Returns detailed validation result with all issues documented
 */
export function validateWorksheetStructure(
  worksheet: XLSX.WorkSheet,
  sheetName: string,
  expectedType?: 'upfront' | 'trail' | 'nil' | 'summary'
): WorksheetValidationResult {
  const issues: string[] = [];

  // Check worksheet exists and has data
  if (!worksheet || !worksheet['!ref']) {
    return {
      isValid: false,
      headers: [],
      rowCount: 0,
      issues: [`Worksheet "${sheetName}" is empty or invalid`],
      metadata: {
        hasFormulaColumns: false,
        hasMergedCells: false,
        estimatedDataRows: 0,
        emptyHeaderCount: 0,
        suspiciousHeaderCount: 0
      }
    };
  }

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  const rowCount = range.e.r - range.s.r + 1;

  // Validate minimum row count (header + at least 1 data row)
  if (rowCount < 2) {
    issues.push(`Worksheet has only ${rowCount} row(s), expected at least 2 (header + data)`);
  }

  // Check for merged cells (can cause parsing issues)
  const hasMergedCells = !!(worksheet['!merges'] && worksheet['!merges'].length > 0);
  if (hasMergedCells) {
    issues.push('Worksheet contains merged cells which may cause parsing issues');
  }

  // Validate headers
  let headers: string[] = [];
  let emptyHeaderCount = 0;
  let suspiciousHeaderCount = 0;

  try {
    headers = validateWorksheetHeaders(worksheet, sheetName);

    // Count empty headers (columns that were filtered out)
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    const totalColumns = range.e.c - range.s.c + 1;
    emptyHeaderCount = totalColumns - headers.length;

    // Count suspicious headers (very long)
    suspiciousHeaderCount = headers.filter(h => h.length > 100).length;

  } catch (error) {
    issues.push((error as Error).message);
  }

  // Type-specific validation
  if (expectedType && headers.length > 0) {
    const requiredColumns = getRequiredColumnsForType(expectedType);
    const missingColumns = requiredColumns.filter(
      col => !headers.some(h => h.toLowerCase().includes(col.toLowerCase()))
    );

    if (missingColumns.length > 0) {
      issues.push(
        `Missing expected columns for ${expectedType} sheet: ${missingColumns.join(', ')}`
      );
    }
  }

  // Check for formula columns (may need special handling)
  const hasFormulaColumns = checkForFormulas(worksheet, range);
  if (hasFormulaColumns) {
    console.info(`Worksheet "${sheetName}" contains formula columns`);
  }

  // Estimate actual data rows (excluding empty rows)
  const estimatedDataRows = countNonEmptyRows(worksheet, range);

  // Warn if many empty rows
  if (estimatedDataRows < rowCount * 0.5 && rowCount > 10) {
    issues.push(
      `Worksheet appears to have many empty rows: ${estimatedDataRows} non-empty out of ${rowCount - 1} total data rows`
    );
  }

  return {
    isValid: issues.length === 0,
    headers,
    rowCount,
    issues,
    metadata: {
      hasFormulaColumns,
      hasMergedCells,
      estimatedDataRows,
      emptyHeaderCount,
      suspiciousHeaderCount
    }
  };
}

/**
 * Safe header extraction with fallback
 *
 * Use this for UI display where you need a safe fallback
 * For validation, use validateWorksheetHeaders() instead
 */
export function extractHeadersSafely(
  worksheet: XLSX.WorkSheet,
  sheetName: string,
  headerRowIndex: number = 0
): { headers: string[]; hasIssues: boolean; issues: string[] } {
  const issues: string[] = [];

  try {
    // Get all rows
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      defval: ''
    });

    if (!jsonData || jsonData.length === 0) {
      return {
        headers: [],
        hasIssues: true,
        issues: [`Worksheet "${sheetName}" is empty`]
      };
    }

    if (headerRowIndex >= jsonData.length) {
      return {
        headers: [],
        hasIssues: true,
        issues: [`Header row index ${headerRowIndex} exceeds worksheet length ${jsonData.length}`]
      };
    }

    const headerRow = jsonData[headerRowIndex] as any[];

    if (!headerRow || headerRow.length === 0) {
      return {
        headers: [],
        hasIssues: true,
        issues: [`Header row ${headerRowIndex} is empty`]
      };
    }

    // Extract headers with validation
    const headers: string[] = [];
    let emptyCount = 0;

    headerRow.forEach((cell, index) => {
      if (cell === undefined || cell === null || String(cell).trim() === '') {
        emptyCount++;
        return;
      }

      const headerStr = String(cell).trim();
      headers.push(headerStr);

      // Check for suspicious headers
      if (headerStr.length > 100) {
        issues.push(`Column ${index + 1} has unusually long header (${headerStr.length} chars)`);
      }
    });

    if (emptyCount > 0) {
      issues.push(`${emptyCount} empty or invalid header(s) were skipped`);
    }

    if (headers.length === 0) {
      issues.push('No valid headers found in header row');
    }

    return {
      headers,
      hasIssues: issues.length > 0,
      issues
    };

  } catch (error) {
    return {
      headers: [],
      hasIssues: true,
      issues: [`Error extracting headers: ${(error as Error).message}`]
    };
  }
}
